import {
  faMagnifyingGlass,
  faUtensils,
  type IconDefinition,
} from '@fortawesome/pro-regular-svg-icons';

import {CuisineTaxonEnum} from '@/components/features/search/filters/FiltersUtils/cuisines';
import {
  type FilterState,
  FiltersUpdateSource,
} from '@/components/features/search/filters/FiltersUtils/types';
import {type PartialTypeaheadResult} from '@/components/global/layout/AppBar/SearchBar/types';
import {
  CuisineOptionEnum,
  CuratedListName,
  SearchServiceCapabilitiesTaxonomy,
  SearchSuitabilityTaxonomy,
  TypeaheadDocType,
} from '@/graphql/types';

// The partial filter (plus its tracking source) a suggested-search chip applies on submit.
export type StagedSearchFilter = {filters: Partial<FilterState>; source: FiltersUpdateSource};

// True while every entry a staged suggested search applied is still present in the committed
// filters. Goes false once the matching chip is removed or filters are cleared — the signal the
// What field uses to drop a suggested-search label it can no longer represent.
export const isStagedFilterActive = (
  staged: StagedSearchFilter,
  committedFilters: FilterState,
): boolean =>
  Object.entries(staged.filters).every(
    ([key, value]) => committedFilters[key as keyof FilterState] === value,
  );

// A cuisine or diversity typeahead result carries a structured filter, so selecting it should run a
// filtered search (cuisineOption / deiOwnedBusinessTaxonomy) rather than a keyword search.
export const typeaheadResultToStagedFilter = (
  result: PartialTypeaheadResult,
): StagedSearchFilter | null => {
  switch (result.type) {
    case TypeaheadDocType.Cuisine:
      return {filters: {cuisineOption: result.id}, source: FiltersUpdateSource.CuisineOption};
    case TypeaheadDocType.Diversity:
      return {filters: {deiOwnedBusinessTaxonomy: result.id}, source: FiltersUpdateSource.Dei};
    default:
      return null;
  }
};

export type WhatSuggestion = {
  key: string;
  icon: IconDefinition;
  filter: StagedSearchFilter;
};

export const WHAT_SUGGESTIONS: WhatSuggestion[] = [
  {
    key: CuisineTaxonEnum.Asian,
    icon: faUtensils,
    filter: {
      filters: {cuisineOption: CuisineOptionEnum.Asian},
      source: FiltersUpdateSource.CuisineOption,
    },
  },
  {
    key: CuisineTaxonEnum.Breakfast,
    icon: faUtensils,
    filter: {
      filters: {cuisineOption: CuisineOptionEnum.Breakfast},
      source: FiltersUpdateSource.CuisineOption,
    },
  },
  {
    key: CuisineTaxonEnum.Bbq,
    icon: faUtensils,
    filter: {
      filters: {cuisineOption: CuisineOptionEnum.Bbq},
      source: FiltersUpdateSource.CuisineOption,
    },
  },
  {
    key: CuisineTaxonEnum.Italian,
    icon: faUtensils,
    filter: {
      filters: {cuisineOption: CuisineOptionEnum.Italian},
      source: FiltersUpdateSource.CuisineOption,
    },
  },
  {
    key: CuisineTaxonEnum.Mediterranean,
    icon: faUtensils,
    filter: {
      filters: {cuisineOption: CuisineOptionEnum.Mediterranean},
      source: FiltersUpdateSource.CuisineOption,
    },
  },
  {
    key: CuisineTaxonEnum.Sandwiches,
    icon: faUtensils,
    filter: {
      filters: {cuisineOption: CuisineOptionEnum.Sandwiches},
      source: FiltersUpdateSource.CuisineOption,
    },
  },
  {
    key: CuratedListName.ReliabilityRockstar,
    icon: faMagnifyingGlass,
    filter: {
      filters: {reliabilityRockstar: 'true'},
      source: FiltersUpdateSource.ReliabilityRockstar,
    },
  },
  {
    key: SearchServiceCapabilitiesTaxonomy.GroupOrdering,
    icon: faMagnifyingGlass,
    filter: {
      filters: {serviceCapabilitiesTaxonomy: SearchServiceCapabilitiesTaxonomy.GroupOrdering},
      source: FiltersUpdateSource.GroupOrdering,
    },
  },
  {
    key: SearchSuitabilityTaxonomy.NewBusiness,
    icon: faMagnifyingGlass,
    filter: {
      filters: {suitabilityTaxonomy: SearchSuitabilityTaxonomy.NewBusiness},
      source: FiltersUpdateSource.NewBusiness,
    },
  },
];
